import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { ApiError, keys, signIn } from "../api";
import { usePageTitle } from "../components/ui";

/**
 * The shared-password screen: the splash art, one password box, nothing else (Tyler, 2026-09-19).
 * A visually hidden username field lets password managers offer to save and fill the password.
 */
export function SignInPage() {
  usePageTitle("Sign in");
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const state = await signIn(password);
      queryClient.setQueryData(keys.session, state);
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status !== 500 ? e.message : "Could not reach the server.",
      );
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="sign-in">
      <img className="sign-in-art" src="/splash.png" alt="BreweryLogistics" />
      <form
        className="sign-in-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input
          className="sr-only"
          type="text"
          name="username"
          autoComplete="username"
          value="White Oak"
          readOnly
          tabIndex={-1}
          aria-hidden
        />
        <div className="sign-in-box">
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            aria-label="Password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            // biome-ignore lint/a11y/noAutofocus: the password is the only thing on the page
            autoFocus
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "sign-in-error" : undefined}
          />
          <button type="submit" aria-label="Sign in" disabled={!password || busy}>
            <ArrowRight size={18} />
          </button>
        </div>
        <p className="sign-in-error" id="sign-in-error" role="alert">
          {error ?? ""}
        </p>
      </form>
    </main>
  );
}
